import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { UserAvatar, fmtMinutes } from "@/components/TaskShared";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AgencyNotifications, useAgencyNotifFeed } from "@/components/AgencyNotifications";
import { TransmissionById } from "@/components/TransmissionModal";
import { toast } from "sonner";
import { Loader2, ListChecks, Eye, Bell, Clock, Check } from "lucide-react";

function dueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00"); const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  const txt = d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  if (diff < 0) return { txt, tone: "text-rose-600", overdue: true };
  if (diff === 0) return { txt: "Today", tone: "text-[#d19100]" };
  if (diff <= 3) return { txt, tone: "text-[#d19100]" };
  return { txt, tone: "text-[#9FA1A4]" };
}
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now"; if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`;
}

export default function ConsoleInbox() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [openTask, setOpenTask] = useState(null);
  const [txId, setTxId] = useState(null);
  const feed = useAgencyNotifFeed();

  const load = async () => { const { data } = await api.get("/console/inbox"); setData(data); };
  useEffect(() => {
    load();
    api.get("/agency/users").then(({ data }) => setUsers(data.filter((u) => u.status !== "invited"))).catch(() => {});
  }, []);

  const onNotifNav = async (n) => {
    if (n.type === "transmission" && n.link) { setTxId(n.link); feed.load(); return; }
    if (typeof n.link === "string" && n.link.startsWith("task:")) { setOpenTask(n.link.slice(5)); feed.load(); return; }
    if (typeof n.link === "string" && n.link.startsWith("social:")) { window.location.assign(`/console/social/${n.link.slice(7)}`); return; }
    if (typeof n.link === "string" && n.link.startsWith("doc:")) { window.location.assign(`/console/docs/${n.link.slice(4)}`); return; }
    if (n.workspace_id && n.workspace_id !== "agency") {
      try {
        const { data } = await api.post("/auth/enter-workspace", { workspace_id: n.workspace_id });
        localStorage.setItem("mc_ws_token", data.token);
        navigate(n.link || (n.conversation_id ? `/${data.slug}/comms?c=${n.conversation_id}` : n.ticket_id ? `/${data.slug}/tickets` : `/${data.slug}`));
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    } else if (n.link) {
      localStorage.setItem("console_nav", n.link);
      window.location.assign("/console");
    }
  };

  if (!data) return <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>;
  const c = data.counts || {};

  return (
    <main className="px-6 md:px-10 py-10 max-w-4xl mx-auto" data-testid="console-inbox-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Inbox</h1>
        <p className="mt-2 text-sm text-[#9FA1A4]">Your tasks, follows and notifications across every client.</p>
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="bg-[#F6F6FD] border border-[#EEEFEF]">
          <TabsTrigger value="mine" data-testid="inbox-tab-mine"><ListChecks className="h-4 w-4 mr-1.5" /> My tasks{c.my_tasks ? <span className="ml-1.5 font-mono-data text-[10px] bg-[#EDE7FF] text-[#5b2fd6] rounded-full px-1.5">{c.my_tasks}</span> : null}</TabsTrigger>
          <TabsTrigger value="following" data-testid="inbox-tab-following"><Eye className="h-4 w-4 mr-1.5" /> Following{c.following ? <span className="ml-1.5 font-mono-data text-[10px] bg-[#EDE7FF] text-[#5b2fd6] rounded-full px-1.5">{c.following}</span> : null}</TabsTrigger>
          <TabsTrigger value="notifs" data-testid="inbox-tab-notifs"><Bell className="h-4 w-4 mr-1.5" /> Notifications{feed.unread ? <span className="ml-1.5 font-mono-data text-[10px] bg-[#F95B5F] text-white rounded-full px-1.5">{feed.unread}</span> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="mine" className="pt-5">
          <TaskList tasks={data.my_tasks} onOpen={setOpenTask} onComplete={load} emptyText="No open tasks assigned to you. Enjoy the calm." />
        </TabsContent>
        <TabsContent value="following" className="pt-5">
          <TaskList tasks={data.following} onOpen={setOpenTask} onComplete={load} emptyText="You're not following any active tasks right now." />
        </TabsContent>
        <TabsContent value="notifs" className="pt-5">
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden">
            <AgencyNotifications feed={feed} onNavigate={onNotifNav} />
            {txId && <TransmissionById id={txId} onClose={() => { setTxId(null); feed.load(); }} />}
          </div>
        </TabsContent>
      </Tabs>

      {openTask && <TaskDetailDialog taskId={openTask} users={users} onClose={() => setOpenTask(null)} onChanged={load} />}
    </main>
  );
}

function TaskList({ tasks, onOpen, onComplete, emptyText }) {
  if (!tasks || tasks.length === 0) return <Empty text={emptyText} icon={ListChecks} />;
  const complete = async (e, t) => {
    e.stopPropagation();
    const prev = t.status;
    try {
      await api.put(`/console/tasks/${t.id}`, { status: "Completed", quick: true });
      toast.success("Marked complete", { action: { label: "Undo", onClick: async () => { try { await api.put(`/console/tasks/${t.id}`, { status: prev }); onComplete && onComplete(); } catch (err) {} } } });
      onComplete && onComplete();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white overflow-hidden divide-y divide-[#f6f6f9]">
      {tasks.map((t) => {
        const due = dueLabel(t.due_date);
        return (
          <div key={t.id} data-testid={`inbox-task-${t.id}`} onClick={() => onOpen(t.id)} className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#FCFCFE] transition-colors cursor-pointer">
            {t.status !== "Completed" && <button data-testid={`inbox-complete-${t.id}`} onClick={(e) => complete(e, t)} title="Mark complete" className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[#c4c5cc] border border-[#EEEFEF] bg-white hover:text-white hover:bg-[#30BD79] hover:border-[#30BD79] transition-colors"><Check className="h-4 w-4" /></button>}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#06060C] truncate">{t.title}</p>
              <p className="text-[11px] text-[#9FA1A4] font-mono-data mt-0.5 truncate">
                {t.client?.name}{t.project_name ? ` · ${t.project_name}` : ""}{t.time_minutes ? ` · ${fmtMinutes(t.time_minutes)}` : ""}
              </p>
            </div>
            <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#595D62] bg-[#F6F6FD] border border-[#EEEFEF] rounded-full px-2 py-0.5 shrink-0">{t.status}</span>
            {due && <span className={`flex items-center gap-1 text-xs shrink-0 ${due.tone}`}><Clock className="h-3.5 w-3.5" />{due.txt}</span>}
          </div>
        );
      })}
    </div>
  );
}

function Empty({ text, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-12 text-center">
      <div className="h-12 w-12 mx-auto rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-3"><Icon className="h-5 w-5" /></div>
      <p className="text-sm text-[#9FA1A4]">{text}</p>
    </div>
  );
}
